/**
 * DisasterFundingCard
 *
 * Pure, compositional component for displaying Disaster Emergency Fund (DEFC)
 * information using the DataSheet component for structured key-value display.
 *
 * Shows for awards with disaster/emergency funding:
 * - COVID-19 relief (CARES Act, American Rescue Plan, etc.)
 * - Infrastructure Investment (IIJA/Bipartisan Infrastructure Law)
 * - Per-DEFC breakdowns with codes, obligations, and outlays
 *
 * Data Sources:
 * - spending_by_defc nested field on award_search
 * - disaster_emergency_fund_codes array on award
 * - File C (financial_accounts_by_awards) DEFC aggregations
 */
import * as React from 'react'

import { AlertTriangle, ExternalLink } from 'lucide-react'

import { DataSheet } from '@/components/opportunity/common'

import type { DefcBreakdown, DefcData } from '@/types/usaspending'

import { formatCurrency } from '../../common/transforms'

// Re-export types for convenience
export type { DefcBreakdown, DefcData }

// ============================================================================
// Constants
// ============================================================================

/**
 * DEFC code metadata for display.
 * Maps codes to public law names for user-friendly display.
 */
const DEFC_METADATA: Record<
  string,
  { name: string; group: 'covid' | 'iija' | 'other' }
> = {
  L: { name: 'CARES Act', group: 'covid' },
  M: { name: 'Families First Coronavirus Response Act', group: 'covid' },
  N: { name: 'Paycheck Protection Program', group: 'covid' },
  O: {
    name: 'Coronavirus Response & Consolidated Appropriations',
    group: 'covid',
  },
  P: { name: 'American Rescue Plan Act', group: 'covid' },
  U: { name: 'COVID-19 Supplemental Appropriations', group: 'covid' },
  '1': { name: 'Infrastructure Investment and Jobs Act', group: 'iija' },
  '7': { name: 'Inflation Reduction Act', group: 'iija' },
  '9': { name: 'Non-Disaster Funding', group: 'other' },
}

/**
 * URL template for DEFC reference on USASpending.
 */
function buildDefcUrl(code: string): string {
  // USASpending has disaster pages at /disaster/covid-19 etc.
  // For individual DEFC codes, link to the disaster overview
  const group = DEFC_METADATA[code]?.group
  if (group === 'covid') {
    return 'https://www.usaspending.gov/disaster/covid-19'
  }
  if (group === 'iija') {
    return 'https://www.usaspending.gov/disaster/infrastructure'
  }
  return 'https://www.usaspending.gov'
}

// ============================================================================
// Guard Functions
// ============================================================================

/**
 * Check if there's meaningful DEFC data to display.
 * Pure predicate - same input always returns same output.
 */
export function hasDefcData(data: DefcData | null | undefined): boolean {
  if (!data) return false

  // Check for any non-empty DEFC codes or breakdowns with non-zero amounts
  const hasCodes = Array.isArray(data.codes) && data.codes.length > 0
  const hasBreakdowns =
    Array.isArray(data.breakdowns) && data.breakdowns.length > 0
  const hasTotals =
    (typeof data.totalCovid19Obligation === 'number' &&
      data.totalCovid19Obligation !== 0) ||
    (typeof data.totalCovid19Outlay === 'number' &&
      data.totalCovid19Outlay !== 0) ||
    (typeof data.totalIijaObligation === 'number' &&
      data.totalIijaObligation !== 0) ||
    (typeof data.totalIijaOutlay === 'number' && data.totalIijaOutlay !== 0)

  return hasCodes || hasBreakdowns || hasTotals
}

/**
 * Check if the award is likely to have disaster funding based on category.
 * All award categories can potentially have disaster funding through File C.
 */
export function isDefcEligible(_category: string | null | undefined): boolean {
  // All award categories can have DEFC funding
  // The actual check is whether the data exists
  return true
}

// ============================================================================
// Row Data Builders
// ============================================================================

interface RowData {
  label: string
  value: React.ReactNode
  code?: string
  highlight?: boolean
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value)
}

interface TotalInfo {
  value: number | null
  hasBreakdowns: boolean
  hasUnknown: boolean
}

function getGroupTotal(
  data: DefcData,
  group: 'covid' | 'iija',
  field: 'obligation' | 'outlay',
  providedTotal?: number | null,
): TotalInfo {
  const breakdowns =
    data.breakdowns?.filter((b) => DEFC_METADATA[b.code]?.group === group) ?? []

  if (isNumber(providedTotal)) {
    return {
      value: providedTotal,
      hasBreakdowns: breakdowns.length > 0,
      hasUnknown: false,
    }
  }

  let sum = 0
  let hasNumber = false
  let hasUnknown = false

  for (const breakdown of breakdowns) {
    const amount = breakdown[field]
    if (isNumber(amount)) {
      sum += amount
      hasNumber = true
    } else if (amount == null) {
      hasUnknown = true
    }
  }

  if (hasUnknown) {
    return { value: null, hasBreakdowns: breakdowns.length > 0, hasUnknown }
  }

  return {
    value: hasNumber ? sum : null,
    hasBreakdowns: breakdowns.length > 0,
    hasUnknown,
  }
}

/**
 * Build summary rows for totals.
 * Pure function - same input produces same output.
 */
function buildSummaryRows(data: DefcData): RowData[] {
  const rows: RowData[] = []

  // Calculate totals from breakdowns if not provided
  const covid19Obligation = getGroupTotal(
    data,
    'covid',
    'obligation',
    data.totalCovid19Obligation,
  )
  const covid19Outlay = getGroupTotal(
    data,
    'covid',
    'outlay',
    data.totalCovid19Outlay,
  )
  const iijaObligation = getGroupTotal(
    data,
    'iija',
    'obligation',
    data.totalIijaObligation,
  )
  const iijaOutlay = getGroupTotal(data, 'iija', 'outlay', data.totalIijaOutlay)

  const pushRow = (label: string, total: TotalInfo, highlight: boolean) => {
    if (total.value === null) {
      if (!total.hasBreakdowns) return
      rows.push({
        label,
        value: 'Unknown',
      })
      return
    }
    if (total.value === 0) return
    rows.push({
      label,
      value: formatCurrency(total.value),
      highlight: highlight && total.value > 0,
    })
  }

  // COVID-19 totals
  pushRow('COVID-19 Relief Obligated', covid19Obligation, true)
  pushRow('COVID-19 Relief Disbursed', covid19Outlay, false)

  // IIJA totals
  pushRow('Infrastructure Investment Obligated', iijaObligation, true)
  pushRow('Infrastructure Investment Disbursed', iijaOutlay, false)

  return rows
}

/**
 * Build breakdown rows for per-DEFC details.
 * Pure function - same input produces same output.
 */
function buildBreakdownRows(data: DefcData): RowData[] {
  if (!data.breakdowns || data.breakdowns.length === 0) {
    return []
  }

  // Sort by obligation descending
  const sorted = [...data.breakdowns].sort((a, b) => {
    const aValue = isNumber(a.obligation) ? a.obligation : -Infinity
    const bValue = isNumber(b.obligation) ? b.obligation : -Infinity
    return bValue - aValue
  })

  return sorted.map((breakdown) => {
    const metadata = DEFC_METADATA[breakdown.code]
    const name =
      breakdown.description || metadata?.name || `DEFC ${breakdown.code}`
    const obligationValue = isNumber(breakdown.obligation)
      ? formatCurrency(breakdown.obligation)
      : 'Unknown'
    const showOutlay =
      isNumber(breakdown.outlay) &&
      breakdown.outlay !== 0 &&
      (!isNumber(breakdown.obligation) ||
        breakdown.outlay !== breakdown.obligation)

    return {
      label: name,
      value: (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-primary-900 dark:text-primary-100">
            {obligationValue}
          </span>
          {showOutlay && (
            <span className="text-xs text-primary-500 dark:text-primary-200">
              {formatCurrency(breakdown.outlay)} disbursed
            </span>
          )}
        </div>
      ),
      code: breakdown.code,
    }
  })
}

/**
 * Count distinct disaster fund codes (excluding non-disaster code '9').
 * Uses codes array if available, otherwise falls back to breakdowns count.
 * Pure function - same input produces same output.
 */
function countDisasterFunds(data: DefcData): number {
  // Prefer codes array if available
  if (data.codes && data.codes.length > 0) {
    return new Set(data.codes.filter((c) => c !== '9')).size
  }
  // Fall back to breakdowns count (each breakdown represents a distinct fund)
  if (data.breakdowns && data.breakdowns.length > 0) {
    return new Set(data.breakdowns.map((b) => b.code).filter((c) => c !== '9'))
      .size
  }
  return 0
}

/**
 * Build DEFC code reference rows with links.
 * Pure function - same input produces same output.
 */
function buildCodeReferenceRows(data: DefcData): RowData[] {
  if (!data.codes || data.codes.length === 0) {
    return []
  }

  // Filter out code "9" (non-disaster) and group unique codes
  const disasterCodes = [...new Set(data.codes)].filter((code) => code !== '9')

  if (disasterCodes.length === 0) {
    return []
  }

  return disasterCodes.map((code) => {
    const metadata = DEFC_METADATA[code]
    const url = buildDefcUrl(code)

    return {
      label: `DEFC ${code}`,
      value: (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-accent-600 hover:text-accent-700 hover:underline dark:text-accent-400 dark:hover:text-accent-300"
        >
          {metadata?.name || 'Disaster/Emergency Fund'}
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
      code,
    }
  })
}

// ============================================================================
// Main Component
// ============================================================================

interface DisasterFundingCardProps {
  /** Award category (all categories can have DEFC funding) */
  category: string | null | undefined
  /** DEFC data from award */
  data: DefcData | null | undefined
}

/**
 * DisasterFundingCard - Pure component for disaster/emergency fund display.
 *
 * Renders a DataSheet with DEFC funding information when disaster
 * emergency fund data is present on the award.
 *
 * @example
 * ```tsx
 * <DisasterFundingCard
 *   category="contract"
 *   data={{
 *     codes: ['L', 'P'],
 *     breakdowns: [
 *       { code: 'L', obligation: 50000, outlay: 45000 },
 *       { code: 'P', obligation: 25000, outlay: 20000 },
 *     ],
 *   }}
 * />
 * ```
 */
export const DisasterFundingCard: React.FC<DisasterFundingCardProps> = ({
  category,
  data,
}) => {
  // Guard: Check eligibility and data presence
  if (!isDefcEligible(category)) return null
  if (!hasDefcData(data)) return null

  // Build row data from DEFC data
  const summaryRows = buildSummaryRows(data!)
  const breakdownRows = buildBreakdownRows(data!)
  const codeRows = buildCodeReferenceRows(data!)

  // Only render if we have rows to show
  const hasContent =
    summaryRows.length > 0 || breakdownRows.length > 0 || codeRows.length > 0

  if (!hasContent) return null

  const fundCount = countDisasterFunds(data!)

  return (
    <DataSheet
      title="Disaster & Emergency Funding"
      icon={<AlertTriangle />}
      badge={
        fundCount > 0
          ? `${fundCount} fund${fundCount === 1 ? '' : 's'}`
          : undefined
      }
    >
      {/* Summary Section */}
      {summaryRows.length > 0 && (
        <DataSheet.Section title="Funding Summary">
          {summaryRows.map((row, index) => (
            <DataSheet.Row
              key={`summary-${row.label}-${index}`}
              label={row.label}
              value={row.value}
              highlight={row.highlight}
            />
          ))}
        </DataSheet.Section>
      )}

      {/* Per-DEFC Breakdown Section */}
      {breakdownRows.length > 0 && (
        <DataSheet.Section
          title="By Fund Code"
          collapsible
          defaultCollapsed={breakdownRows.length > 3}
        >
          {breakdownRows.map((row, index) => (
            <DataSheet.Row
              key={`breakdown-${row.code}-${index}`}
              label={row.label}
              value={row.value}
              code={row.code}
            />
          ))}
        </DataSheet.Section>
      )}

      {/* DEFC Code References (if no breakdowns, show code list) */}
      {breakdownRows.length === 0 && codeRows.length > 0 && (
        <DataSheet.Section title="Fund Codes">
          {codeRows.map((row, index) => (
            <DataSheet.Row
              key={`code-${row.code}-${index}`}
              label={row.label}
              value={row.value}
              code={row.code}
            />
          ))}
        </DataSheet.Section>
      )}
    </DataSheet>
  )
}

DisasterFundingCard.displayName = 'DisasterFundingCard'

export type { DisasterFundingCardProps }
